import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChartBarIcon,
  UserGroupIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

interface DashboardStats {
  totalUsers: number;
  totalLawsuits: number;
  activeLawsuits: number;
  paidLawsuits: number;
  totalRevenue: number;
}

interface RecentActivity {
  users: Array<{
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    createdAt: string;
  }>;
  lawsuits: Array<{
    id: string;
    title: string;
    category: string;
    status: string;
    createdAt: string;
  }>;
}

interface Analytics {
  lawsuitsByCategory: Array<{ _id: string; count: number }>;
  lawsuitsByCountry: Array<{ _id: string; count: number }>;
  lawsuitsByStatus: Array<{ _id: string; count: number }>;
}

const AnalyticsDashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentActivity, setRecentActivity] = useState<RecentActivity | null>(null);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get<{
          success: boolean;
          data: {
            stats: DashboardStats;
            recentActivity: RecentActivity;
            analytics: Analytics;
          };
        }>('/api/admin/dashboard');

        setStats(response.data.data.stats);
        setRecentActivity(response.data.data.recentActivity);
        setAnalytics(response.data.data.analytics);
      } catch (err: any) {
        setError(err.message || t('admin.fetchError', 'Failed to fetch dashboard data'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, [t]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount / 100);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <DocumentTextIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      {t('admin.totalLawsuits', 'Total Lawsuits')}
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {stats.totalLawsuits}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ChartBarIcon className="h-6 w-6 text-green-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      {t('admin.activeLawsuits', 'Active Lawsuits')}
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {stats.activeLawsuits}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <UserGroupIcon className="h-6 w-6 text-blue-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      {t('admin.totalUsers', 'Total Users')}
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {stats.totalUsers.toLocaleString()}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CurrencyDollarIcon className="h-6 w-6 text-green-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      {t('admin.totalRevenue', 'Total Revenue')}
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {formatCurrency(stats.totalRevenue)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Analytics Charts */}
      {analytics && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {t('admin.lawsuitsByCategory', 'Lawsuits by Category')}
            </h3>
            <div className="space-y-3">
              {analytics.lawsuitsByCategory.map((item) => (
                <div key={item._id} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 capitalize">
                    {item._id.replace('_', ' ')}
                  </span>
                  <span className="text-sm font-medium text-gray-900">{item.count}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {t('admin.lawsuitsByCountry', 'Lawsuits by Country')}
            </h3>
            <div className="space-y-3">
              {analytics.lawsuitsByCountry.map((item) => (
                <div key={item._id} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{item._id}</span>
                  <span className="text-sm font-medium text-gray-900">{item.count}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {t('admin.lawsuitsByStatus', 'Lawsuits by Status')}
            </h3>
            <div className="space-y-3">
              {analytics.lawsuitsByStatus.map((item) => (
                <div key={item._id} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 capitalize">
                    {item._id.replace('_', ' ')}
                  </span>
                  <span className="text-sm font-medium text-gray-900">{item.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Recent Activity */}
      {recentActivity && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {t('admin.recentLawsuits', 'Recent Lawsuits')}
              </h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {recentActivity.lawsuits.map((lawsuit) => (
                  <div key={lawsuit.id} className="border border-gray-200 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-gray-900 truncate">
                      {lawsuit.title}
                    </h3>
                    <div className="mt-1 text-xs text-gray-500">
                      <span className="capitalize">{lawsuit.category.replace('_', ' ')}</span>
                      {' • '}
                      <span className="capitalize">{lawsuit.status.replace('_', ' ')}</span>
                      {' • '}
                      <span>{new Date(lawsuit.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {t('admin.recentUsers', 'Recent Users')}
              </h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {recentActivity.users.map((user) => (
                  <div key={user.id} className="border border-gray-200 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-gray-900">
                      {user.firstName} {user.lastName}
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">{user.email}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {t('admin.joined', 'Joined')} {new Date(user.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboardPage;
